import {
  isLineBreakElement,
  isStringElement,
} from '@packages/utilities/elementTypeUtils';
import {Expressions} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {collectTextFromDescendants} from './collectTextFromDescendants';

const CHILDREN_PATH = [{in: 'Record' as const, at: 'children'}];

function getDirectChildren(element: ExpressionElement) {
  const childrenExpr = Expressions.getIn(
    Expressions.getElementData(element),
    CHILDREN_PATH
  );
  return childrenExpr ? Expressions.getList(childrenExpr) : null;
}

/**
 * Returns true if the element has at least one inline child (non-String, non-LineBreak).
 * Used to decide whether XML tag wrapping is needed during translation.
 */
export function hasInlineChildren(element: ExpressionElement): boolean {
  const children = getDirectChildren(element);
  if (!children) return false;
  return children.some((child) => {
    const childEl = Expressions.getElement(child);
    if (!childEl) return false;
    return !isStringElement(childEl) && !isLineBreakElement(childEl);
  });
}

/**
 * Collects text from direct children of a typographic element, wrapping inline
 * elements (Span, Link, etc.) in XML tags: `<n elementId>innerText</n>`.
 * String children are collected as plain text. LineBreak children become `\n`.
 *
 * Used when a typographic element has inline children so the translation pipeline
 * can preserve and remap those inline elements after translation.
 */
export function collectTextFromDescendantsWithXmlWrapping(
  element: ExpressionElement
): string {
  const children = getDirectChildren(element);
  if (!children) return '';

  const parts: string[] = [];
  for (const child of children) {
    const childEl = Expressions.getElement(child);
    if (!childEl) continue;

    if (isLineBreakElement(childEl)) {
      parts.push('\n');
      continue;
    }

    if (isStringElement(childEl)) {
      const text = Expressions.getText(Expressions.getElementData(childEl));
      if (text) parts.push(text);
      continue;
    }

    // Inline element (Span, Link, etc.) — wrap with XML tag for translation tracking
    const innerText = collectTextFromDescendants(childEl);
    if (innerText) {
      parts.push(`<n ${Expressions.getElementId(childEl)}>${innerText}</n>`);
    }
  }

  return parts.join('');
}
